import React from "react";
import ControlledFormField from "@/model/interface/ControllerRangeSlider";
import { useFormContext, useWatch } from "react-hook-form";
import { formatNumber } from "@/util/sums";
import { CalculatorField, CalculatorFieldType } from "@/model/interface/CalculatorTypes";
import { ApplicationType } from "@/enum/ApplicationType";

const propertyYearlyAppreciationRate = 8.29;

type ProductInformationConfig = {
  [key in ApplicationType]: {
    title: string;
    fieldsWatched: CalculatorFieldType[];
    calculateFn: (depsArray: number[]) => { homeValueIncreaseOverTime: number; fintreePercentage: number };
  };
};

const getCorrespondingFields = (inputType: ApplicationType) => {
  const Fields: ProductInformationConfig = {
    [ApplicationType.HomeownerAssistant]: {
      title: "Upfront Boost",
      fieldsWatched: [CalculatorField.HomeValue, CalculatorField.MonthlyRepayment, CalculatorField.HelpPercentage],
      calculateFn: (depsArray: number[]) => {
        const [homeValue, monthlyRepayment, helpPercentage] = depsArray;

        const share = (helpPercentage * monthlyRepayment) / 100;
        const contributionOverTwoYears = (share * 24) / homeValue;
        const homeValueIncreaseOverTime = ((contributionOverTwoYears * 100) / propertyYearlyAppreciationRate) * 12;

        const fintreePercentage = Number(100 - Number(formatNumber(contributionOverTwoYears * 100, 2, false)));

        return { homeValueIncreaseOverTime, fintreePercentage };
      },
    },
    [ApplicationType.HomeownershipAccelerator]: {
      title: "Monthly Boost",
      fieldsWatched: [CalculatorField.HomeValue, CalculatorField.DepositNeeded],
      calculateFn: (depsArray: number[]) => {
        const [homeValue, depositBoostValue] = depsArray;

        const share = depositBoostValue / homeValue;
        const homeValueIncreaseOverTime = ((share * 100) / propertyYearlyAppreciationRate) * 12;

        const fintreePercentage = Number(100 - Number(formatNumber(share * 100, 2, false)));

        return { homeValueIncreaseOverTime, fintreePercentage };
      },
    },
    [ApplicationType.UpgradeAccelerator]: {
      title: "Double Boost",
      fieldsWatched: [CalculatorField.HomeValue, CalculatorField.UpgradeBudget],
      calculateFn: (depsArray: number[]) => {
        const [homeValue, buyerBankShareValue] = depsArray;

        const fintreeShareValue = homeValue - buyerBankShareValue;
        const share = fintreeShareValue / homeValue;
        const homeValueIncreaseOverTime = ((share * 100) / propertyYearlyAppreciationRate) * 12;

        const fintreePercentage = Number(100 - Number(formatNumber(share * 100, 2, false)));

        return { homeValueIncreaseOverTime, fintreePercentage };
      },
    },
  };

  const result = Fields[inputType];
  if (!result) {
    throw new Error("Product information component has no configuration specified for input application type");
  }

  return result;
};

interface ProductShareParams extends ControlledFormField {
  applicationType: ApplicationType;
}

const ProductShareInformation: React.FC<ProductShareParams> = ({ fieldPrefix, applicationType }) => {
  const { control } = useFormContext();

  const { title, fieldsWatched, calculateFn } = getCorrespondingFields(applicationType);

  const dependencies = fieldPrefix ? fieldsWatched.map((field) => `${fieldPrefix}.${field}`) : fieldsWatched;
  const depsArray = useWatch({ control, name: dependencies });

  const { homeValueIncreaseOverTime, fintreePercentage } = calculateFn(depsArray);

  return (
    <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-3">
      <h3 className="text-lg font-bold text-gray-900">{title} information</h3>
      <p className="text-sm text-gray-700 leading-relaxed">
        After partnering with BuySooner on this product you will still own{" "}
        <strong className="text-gray-900">{fintreePercentage.toFixed(2)}%</strong> of your property.*
      </p>
      <p className="text-sm text-gray-700 leading-relaxed">
        Did you know that property has appreciated on average 8.29% per year for the last 60 years? Based on this your
        property value should have increased in value to more than what Fintree owns after{" "}
        <strong className="text-gray-900">{formatNumber(homeValueIncreaseOverTime, 2, false)}</strong> months.
      </p>
      <p className="text-xs text-gray-400 text-right">*terms and conditions apply</p>
    </div>
  );
};

export default ProductShareInformation;
